import { useState } from "react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import PartnerAgreement from "../../components/PartnerAgreement";
import {
  MessageSquare, Filter, UserCircle, UserCog, Plus, Pencil, Trash2,
  Upload, FileSpreadsheet, Loader2, Check, RefreshCw, Mail, Phone,
  Building2, DollarSign, Tag, Clock, FileText, ChevronRight, UserPlus, Wand2
} from "lucide-react";

const PERMISSION_LABELS = {
  crm: "CRM & Lead Management",
  users: "View Users",
  memberships: "View Memberships",
  businesses: "Manage Businesses",
  affiliates: "Manage Affiliates",
  applications: "Manage Applications",
  marketing: "AI Marketing & Email",
  analytics: "View Analytics",
  territories: "Manage Territories",
  sales: "View Sales"
};

const CrmTab = ({
  crmLeads,
  teamMembers,
  subLocations,
  onCreateLead,
  onUpdateLead,
  onDeleteLead,
  onAddNote,
  onUpdateLeadStatus,
  onConvertLead,
  onCreateTeamMember,
  onEditTeamMember,
  onDeleteTeamMember,
  onResendInvite,
  onAssignLead,
  onUploadLeads,
  onAIExtractForConvert,
  uploadingLeads,
  converting
}) => {
  // Local state
  const [crmFilter, setCrmFilter] = useState("all");
  const [assigneeFilter, setAssigneeFilter] = useState("all");
  const [selectedLead, setSelectedLead] = useState(null);
  const [leadNote, setLeadNote] = useState("");
  
  // Dialog states
  const [showLeadDialog, setShowLeadDialog] = useState(false);
  const [showTeamDialog, setShowTeamDialog] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showConvertDialog, setShowConvertDialog] = useState(false);
  const [showAgreement, setShowAgreement] = useState(false);
  
  // Form states
  const [editingLead, setEditingLead] = useState(null);
  const [editingMember, setEditingMember] = useState(null);
  const [newLead, setNewLead] = useState({ name: "", email: "", phone: "", company: "", type: "member", status: "new", source: "manual", value: 0, tags: [] });
  const [newMember, setNewMember] = useState({ name: "", email: "", role: "Sales Rep", phone: "", avatar_color: "#D4AF37", permissions: ["crm"], send_invite: true });
  const [convertData, setConvertData] = useState({ description: "", address: "", category: "services", discount_level: 15, sub_location: "", website: "", image_url: "" });
  const [uploadResult, setUploadResult] = useState(null);

  // Helper functions
  const getStatusColor = (status) => {
    const colors = {
      new: "bg-blue-500/20 text-blue-400",
      contacted: "bg-yellow-500/20 text-yellow-400",
      interested: "bg-purple-500/20 text-purple-400",
      converted: "bg-green-500/20 text-green-400",
      lost: "bg-red-500/20 text-red-400"
    };
    return colors[status] || "bg-white/10 text-white/60";
  };

  const getTypeColor = (type) => {
    const colors = {
      member: "bg-[#D4AF37]/20 text-[#D4AF37]",
      business: "bg-blue-500/20 text-blue-400",
      affiliate: "bg-purple-500/20 text-purple-400"
    };
    return colors[type] || "bg-white/10 text-white/60";
  };

  const getAvatarInitials = (name) => {
    if (!name) return "?";
    return name.split(" ").map(n => n[0]).join("").substring(0, 2).toUpperCase();
  };

  const filteredLeads = crmLeads.filter(l => {
    const matchesStatus = crmFilter === "all" || l.status === crmFilter;
    const matchesAssignee = assigneeFilter === "all" ||
      (assigneeFilter === "unassigned" ? !l.assigned_to : l.assigned_to === assigneeFilter);
    return matchesStatus && matchesAssignee;
  });

  // Handlers
  const handleCreateLead = async () => {
    await onCreateLead(newLead);
    setShowLeadDialog(false);
    setNewLead({ name: "", email: "", phone: "", company: "", type: "member", status: "new", source: "manual", value: 0, tags: [] });
  };

  const handleUpdateLead = async () => {
    if (!editingLead) return;
    await onUpdateLead(editingLead.id, newLead);
    setShowLeadDialog(false);
    setEditingLead(null);
    setNewLead({ name: "", email: "", phone: "", company: "", type: "member", status: "new", source: "manual", value: 0, tags: [] });
  };

  const handleEditLead = (lead) => {
    setEditingLead(lead);
    setNewLead({
      name: lead.name || "",
      email: lead.email || "",
      phone: lead.phone || "",
      company: lead.company || "",
      type: lead.type || "member",
      status: lead.status || "new",
      source: lead.source || "manual",
      value: lead.value || 0,
      tags: lead.tags || []
    });
    setShowLeadDialog(true);
  };

  const handleDeleteLead = async (leadId) => {
    if (!confirm("Are you sure you want to delete this lead?")) return;
    await onDeleteLead(leadId);
    if (selectedLead?.id === leadId) setSelectedLead(null);
  };

  const handleAddNote = async () => {
    if (!selectedLead || !leadNote.trim()) return;
    await onAddNote(selectedLead.id, leadNote);
    setLeadNote("");
  };

  const handleCreateTeamMember = async () => {
    if (!newMember.name || !newMember.email) return;
    if (editingMember) {
      await onEditTeamMember(editingMember.id, newMember);
    } else {
      await onCreateTeamMember(newMember);
    }
    setShowTeamDialog(false);
    setEditingMember(null);
    setNewMember({ name: "", email: "", role: "Sales Rep", phone: "", avatar_color: "#D4AF37", permissions: ["crm"], send_invite: true });
  };

  const handleEditTeamMember = (member) => {
    setEditingMember(member);
    setNewMember({
      name: member.name || "",
      email: member.email || "",
      role: member.role || "Sales Rep",
      phone: member.phone || "",
      avatar_color: member.avatar_color || "#D4AF37",
      permissions: member.permissions || ["crm"],
      send_invite: false
    });
    setShowTeamDialog(true);
  };

  const handleUploadFile = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const result = await onUploadLeads(file);
    if (result) setUploadResult(result);
    event.target.value = '';
  };

  const handleAIExtract = async () => {
    if (!convertData.website) return;
    const data = await onAIExtractForConvert(convertData.website);
    if (data) {
      setConvertData(prev => ({
        ...prev,
        description: data.description || prev.description,
        address: data.address || prev.address,
        category: data.category || prev.category,
        image_url: data.image_url || prev.image_url,
        latitude: data.latitude,
        longitude: data.longitude
      }));
    }
  };

  const handleProceedToAgreement = () => {
    if (!selectedLead?.name) return;
    setShowConvertDialog(false);
    setShowAgreement(true);
  };

  const handleAgreementSigned = async (pdfBlob, discountInfo) => {
    const convertWithDiscount = {
      ...convertData,
      ...(discountInfo?.discount_percent != null ? { discount_level: discountInfo.discount_percent } : {}),
      ...(discountInfo?.discount_applies_to ? { discount_applies_to: discountInfo.discount_applies_to } : {}),
    };
    await onConvertLead(selectedLead, "business", convertWithDiscount, pdfBlob);
    setShowAgreement(false);
    setShowConvertDialog(false);
    setConvertData({ description: "", address: "", category: "services", discount_level: 15, sub_location: "", website: "", image_url: "" });
    setSelectedLead(null);
  };

  const handleConvertToAffiliate = async () => {
    await onConvertLead(selectedLead, "affiliate", null, null);
    setShowConvertDialog(false);
    setSelectedLead(null);
  };

  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Panel - Lead List */}
        <div className="lg:col-span-2 space-y-4">
          {/* CRM Header */}
          <div className="bg-[#0B0F19] rounded-xl border border-[#D4AF37]/10 overflow-hidden">
            <div className="p-4 border-b border-[#D4AF37]/10 flex flex-wrap items-center justify-between gap-3">
              <h3 className="font-['Playfair_Display'] text-xl text-white flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-[#D4AF37]" />
                Lead Management
              </h3>
              <div className="flex flex-wrap items-center gap-2">
                {/* Status Filter */}
                <Select value={crmFilter} onValueChange={setCrmFilter}>
                  <SelectTrigger className="w-[130px] bg-white/5 border-white/10 text-white text-sm">
                    <Filter className="w-4 h-4 mr-2 text-[#D4AF37]" />
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="new">New</SelectItem>
                    <SelectItem value="contacted">Contacted</SelectItem>
                    <SelectItem value="interested">Interested</SelectItem>
                    <SelectItem value="converted">Converted</SelectItem>
                    <SelectItem value="lost">Lost</SelectItem>
                  </SelectContent>
                </Select>

                {/* Assignee Filter */}
                <Select value={assigneeFilter} onValueChange={setAssigneeFilter}>
                  <SelectTrigger className="w-[140px] bg-white/5 border-white/10 text-white text-sm">
                    <UserCircle className="w-4 h-4 mr-2 text-[#D4AF37]" />
                    <SelectValue placeholder="Assignee" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Assignees</SelectItem>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {teamMembers.map(member => (
                      <SelectItem key={member.id} value={member.id}>{member.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Team Management Button */}
                <Dialog open={showTeamDialog} onOpenChange={(open) => {
                  setShowTeamDialog(open);
                  if (!open) {
                    setEditingMember(null);
                    setNewMember({ name: "", email: "", role: "Sales Rep", phone: "", avatar_color: "#D4AF37", permissions: ["crm"], send_invite: true });
                  }
                }}>
                  <DialogTrigger asChild>
                    <Button variant="outline" className="border-white/20 text-white hover:bg-white/5" data-testid="manage-team-btn">
                      <UserCog className="w-4 h-4 mr-2" />
                      Team
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-[#0B0F19] border-[#D4AF37]/20 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle className="font-['Playfair_Display'] text-xl flex items-center gap-2">
                        <UserCog className="w-5 h-5 text-[#D4AF37]" />
                        Manage Team Members
                      </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      {/* Add/Edit Member Form */}
                      <div className="bg-white/5 rounded-lg p-4 space-y-3">
                        <p className="text-white/60 text-sm font-medium">{editingMember ? 'Edit Member' : 'Add New Member'}</p>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-white/70 text-xs">Name *</Label>
                            <Input
                              value={newMember.name}
                              onChange={(e) => setNewMember({ ...newMember, name: e.target.value })}
                              className="bg-white/5 border-white/10 text-white mt-1"
                              placeholder="John Smith"
                            />
                          </div>
                          <div>
                            <Label className="text-white/70 text-xs">Email *</Label>
                            <Input
                              value={newMember.email}
                              onChange={(e) => setNewMember({ ...newMember, email: e.target.value })}
                              className="bg-white/5 border-white/10 text-white mt-1"
                              placeholder="john@company.com"
                              disabled={editingMember?.account_created}
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-white/70 text-xs">Role</Label>
                            <Select value={newMember.role} onValueChange={(v) => setNewMember({ ...newMember, role: v })}>
                              <SelectTrigger className="bg-white/5 border-white/10 text-white mt-1">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Sales Rep">Sales Rep</SelectItem>
                                <SelectItem value="Manager">Manager</SelectItem>
                                <SelectItem value="Admin">Admin</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-white/70 text-xs">Avatar Color</Label>
                            <div className="flex gap-2 mt-1">
                              {['#D4AF37', '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'].map(color => (
                                <button
                                  key={color}
                                  onClick={() => setNewMember({ ...newMember, avatar_color: color })}
                                  className={`w-8 h-8 rounded-full transition-all ${newMember.avatar_color === color ? 'ring-2 ring-white ring-offset-2 ring-offset-[#0B0F19]' : ''}`}
                                  style={{ backgroundColor: color }}
                                />
                              ))}
                            </div>
                          </div>
                        </div>

                        {/* Permissions Section */}
                        <div className="pt-2">
                          <Label className="text-white/70 text-xs block mb-2">Permissions (select what they can access)</Label>
                          <div className="grid grid-cols-2 gap-2">
                            {Object.entries(PERMISSION_LABELS).map(([key, label]) => (
                              <label key={key} className="flex items-center gap-2 p-2 bg-white/5 rounded cursor-pointer hover:bg-white/10 transition-colors">
                                <input
                                  type="checkbox"
                                  checked={newMember.permissions?.includes(key)}
                                  onChange={(e) => {
                                    const perms = newMember.permissions || [];
                                    if (e.target.checked) {
                                      setNewMember({ ...newMember, permissions: [...perms, key] });
                                    } else {
                                      setNewMember({ ...newMember, permissions: perms.filter(p => p !== key) });
                                    }
                                  }}
                                  className="rounded border-white/30 bg-white/5 text-[#D4AF37] focus:ring-[#D4AF37]"
                                />
                                <span className="text-white/80 text-sm">{label}</span>
                              </label>
                            ))}
                          </div>
                        </div>

                        {/* Send Invite Option */}
                        {!editingMember && (
                          <label className="flex items-center gap-2 pt-2">
                            <input
                              type="checkbox"
                              checked={newMember.send_invite}
                              onChange={(e) => setNewMember({ ...newMember, send_invite: e.target.checked })}
                              className="rounded border-white/30 bg-white/5 text-[#D4AF37] focus:ring-[#D4AF37]"
                            />
                            <span className="text-white/80 text-sm">Send invite email to set up their account</span>
                          </label>
                        )}

                        <div className="flex gap-2 pt-2">
                          {editingMember && (
                            <Button
                              variant="outline"
                              onClick={() => {
                                setEditingMember(null);
                                setNewMember({ name: "", email: "", role: "Sales Rep", phone: "", avatar_color: "#D4AF37", permissions: ["crm"], send_invite: true });
                              }}
                              className="flex-1 border-white/20 text-white"
                            >
                              Cancel
                            </Button>
                          )}
                          <Button onClick={handleCreateTeamMember} className="flex-1 btn-gold">
                            {editingMember ? 'Update Member' : 'Add & Send Invite'}
                          </Button>
                        </div>
                      </div>

                      {/* Team Members List */}
                      <div className="space-y-2 max-h-[300px] overflow-y-auto">
                        {teamMembers.length > 0 ? (
                          teamMembers.map(member => (
                            <div key={member.id} className="flex items-center justify-between bg-white/5 rounded-lg p-3">
                              <div className="flex items-center gap-3">
                                <div
                                  className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm"
                                  style={{ backgroundColor: member.avatar_color || '#D4AF37' }}
                                >
                                  {getAvatarInitials(member.name)}
                                </div>
                                <div>
                                  <div className="flex items-center gap-2">
                                    <p className="text-white font-medium">{member.name}</p>
                                    {member.account_created ? (
                                      <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded">Active</span>
                                    ) : member.invite_sent ? (
                                      <span className="text-xs bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded">Invite Sent</span>
                                    ) : (
                                      <span className="text-xs bg-white/10 text-white/50 px-2 py-0.5 rounded">Pending</span>
                                    )}
                                  </div>
                                  <p className="text-white/50 text-xs">{member.role} • {member.email}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-1">
                                {!member.account_created && member.invite_sent && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => onResendInvite(member.id)}
                                    className="text-amber-400 hover:text-amber-300 text-xs"
                                  >
                                    <RefreshCw className="w-3 h-3 mr-1" />
                                    Resend
                                  </Button>
                                )}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleEditTeamMember(member)}
                                  className="text-white/50 hover:text-white"
                                >
                                  <Pencil className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => onDeleteTeamMember(member.id)}
                                  className="text-red-400 hover:text-red-300"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          ))
                        ) : (
                          <p className="text-white/40 text-sm text-center py-4">No team members yet</p>
                        )}
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>

                {/* Upload XLS Button */}
                <Dialog open={showUploadDialog} onOpenChange={(open) => {
                  setShowUploadDialog(open);
                  if (!open) setUploadResult(null);
                }}>
                  <DialogTrigger asChild>
                    <Button variant="outline" className="border-white/20 text-white hover:bg-white/5" data-testid="upload-leads-btn">
                      <Upload className="w-4 h-4 mr-2" />
                      Import
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-[#0B0F19] border-[#D4AF37]/20 text-white max-w-md">
                    <DialogHeader>
                      <DialogTitle className="font-['Playfair_Display'] text-xl flex items-center gap-2">
                        <FileSpreadsheet className="w-5 h-5 text-[#D4AF37]" />
                        Import Leads from Excel
                      </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      <p className="text-white/60 text-sm">
                        Upload an Excel file (.xls or .xlsx) with your leads.
                      </p>
                      <div className="bg-white/5 rounded-lg p-3 text-sm">
                        <p className="text-white/80 font-medium mb-2">Expected columns:</p>
                        <ul className="text-white/60 space-y-1 text-xs">
                          <li>• <span className="text-[#D4AF37]">Name</span> - Contact name (required)</li>
                          <li>• <span className="text-[#D4AF37]">Email</span> - Email address</li>
                          <li>• <span className="text-[#D4AF37]">Phone</span> - Phone number</li>
                          <li>• <span className="text-[#D4AF37]">Company</span> - Business name</li>
                          <li>• <span className="text-[#D4AF37]">Type</span> - member, business, or affiliate</li>
                        </ul>
                      </div>

                      <div className="border-2 border-dashed border-white/20 rounded-lg p-6 text-center hover:border-[#D4AF37]/50 transition-colors">
                        <input
                          type="file"
                          accept=".xls,.xlsx"
                          onChange={handleUploadFile}
                          className="hidden"
                          id="lead-file-upload"
                          disabled={uploadingLeads}
                        />
                        <label htmlFor="lead-file-upload" className="cursor-pointer">
                          {uploadingLeads ? (
                            <div className="flex flex-col items-center">
                              <Loader2 className="w-10 h-10 text-[#D4AF37] mb-2 animate-spin" />
                              <p className="text-white/60">Processing file...</p>
                            </div>
                          ) : (
                            <div className="flex flex-col items-center">
                              <FileSpreadsheet className="w-10 h-10 text-white/40 mb-2" />
                              <p className="text-white/80 font-medium">Click to upload</p>
                              <p className="text-white/40 text-xs mt-1">.xls or .xlsx files</p>
                            </div>
                          )}
                        </label>
                      </div>

                      {uploadResult && (
                        <div className={`rounded-lg p-4 ${uploadResult.created > 0 ? 'bg-green-500/10 border border-green-500/20' : 'bg-yellow-500/10 border border-yellow-500/20'}`}>
                          <div className="flex items-center gap-2 mb-2">
                            <Check className={`w-5 h-5 ${uploadResult.created > 0 ? 'text-green-400' : 'text-yellow-400'}`} />
                            <p className="text-white font-medium">Import Complete</p>
                          </div>
                          <div className="text-sm space-y-1">
                            <p className="text-green-400">{uploadResult.created} leads imported</p>
                            {uploadResult.skipped > 0 && (
                              <p className="text-yellow-400">{uploadResult.skipped} skipped (duplicates or empty)</p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </DialogContent>
                </Dialog>

                {/* Add Lead Button */}
                <Dialog open={showLeadDialog} onOpenChange={(open) => {
                  setShowLeadDialog(open);
                  if (!open) {
                    setEditingLead(null);
                    setNewLead({ name: "", email: "", phone: "", company: "", type: "member", status: "new", source: "manual", value: 0, tags: [] });
                  }
                }}>
                  <DialogTrigger asChild>
                    <Button className="btn-gold px-4 py-2" data-testid="add-lead-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Lead
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-[#0B0F19] border-[#D4AF37]/20 text-white max-w-md">
                    <DialogHeader>
                      <DialogTitle className="font-['Playfair_Display'] text-xl">
                        {editingLead ? 'Edit Lead' : 'Add New Lead'}
                      </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      <div>
                        <Label className="text-white/70">Name *</Label>
                        <Input
                          value={newLead.name}
                          onChange={(e) => setNewLead({ ...newLead, name: e.target.value })}
                          className="bg-white/5 border-white/10 text-white mt-1"
                          placeholder="John Smith"
                          data-testid="lead-name-input"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-white/70">Email</Label>
                          <Input
                            value={newLead.email}
                            onChange={(e) => setNewLead({ ...newLead, email: e.target.value })}
                            className="bg-white/5 border-white/10 text-white mt-1"
                            placeholder="john@email.com"
                            type="email"
                          />
                        </div>
                        <div>
                          <Label className="text-white/70">Phone</Label>
                          <Input
                            value={newLead.phone}
                            onChange={(e) => setNewLead({ ...newLead, phone: e.target.value })}
                            className="bg-white/5 border-white/10 text-white mt-1"
                            placeholder="+34 123 456 789"
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="text-white/70">Company</Label>
                        <Input
                          value={newLead.company}
                          onChange={(e) => setNewLead({ ...newLead, company: e.target.value })}
                          className="bg-white/5 border-white/10 text-white mt-1"
                          placeholder="Company name (optional)"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-white/70">Lead Type</Label>
                          <Select value={newLead.type} onValueChange={(v) => setNewLead({ ...newLead, type: v })}>
                            <SelectTrigger className="bg-white/5 border-white/10 text-white mt-1">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="member">Potential Member</SelectItem>
                              <SelectItem value="business">Business Partner</SelectItem>
                              <SelectItem value="affiliate">Affiliate</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-white/70">Status</Label>
                          <Select value={newLead.status} onValueChange={(v) => setNewLead({ ...newLead, status: v })}>
                            <SelectTrigger className="bg-white/5 border-white/10 text-white mt-1">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="new">New</SelectItem>
                              <SelectItem value="contacted">Contacted</SelectItem>
                              <SelectItem value="interested">Interested</SelectItem>
                              <SelectItem value="converted">Converted</SelectItem>
                              <SelectItem value="lost">Lost</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-white/70">Source</Label>
                          <Select value={newLead.source} onValueChange={(v) => setNewLead({ ...newLead, source: v })}>
                            <SelectTrigger className="bg-white/5 border-white/10 text-white mt-1">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="manual">Manual Entry</SelectItem>
                              <SelectItem value="website">Website</SelectItem>
                              <SelectItem value="referral">Referral</SelectItem>
                              <SelectItem value="event">Event</SelectItem>
                              <SelectItem value="social">Social Media</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-white/70">Est. Value (€)</Label>
                          <Input
                            value={newLead.value}
                            onChange={(e) => setNewLead({ ...newLead, value: parseFloat(e.target.value) || 0 })}
                            className="bg-white/5 border-white/10 text-white mt-1"
                            type="number"
                            min="0"
                          />
                        </div>
                      </div>
                      <Button
                        onClick={editingLead ? handleUpdateLead : handleCreateLead}
                        className="w-full btn-gold py-3"
                        data-testid="save-lead-btn"
                      >
                        {editingLead ? 'Update Lead' : 'Create Lead'}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </div>

            {/* Stats Row */}
            <div className="p-4 grid grid-cols-5 gap-2 border-b border-white/5">
              {[
                { label: "New", count: crmLeads.filter(l => l.status === 'new').length, color: "text-blue-400" },
                { label: "Contacted", count: crmLeads.filter(l => l.status === 'contacted').length, color: "text-yellow-400" },
                { label: "Interested", count: crmLeads.filter(l => l.status === 'interested').length, color: "text-purple-400" },
                { label: "Converted", count: crmLeads.filter(l => l.status === 'converted').length, color: "text-green-400" },
                { label: "Lost", count: crmLeads.filter(l => l.status === 'lost').length, color: "text-red-400" },
              ].map((stat) => (
                <div key={stat.label} className="text-center">
                  <p className={`text-xl font-bold ${stat.color}`}>{stat.count}</p>
                  <p className="text-white/40 text-xs">{stat.label}</p>
                </div>
              ))}
            </div>

            {/* Leads List */}
            <div className="divide-y divide-white/5 max-h-[500px] overflow-y-auto">
              {filteredLeads.length === 0 ? (
                <div className="p-8 text-center text-white/40">
                  <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>No leads yet. Add your first lead to get started!</p>
                </div>
              ) : (
                filteredLeads.map((lead) => (
                  <div
                    key={lead.id}
                    onClick={() => setSelectedLead(lead)}
                    className={`p-4 hover:bg-white/5 cursor-pointer transition-colors ${selectedLead?.id === lead.id ? 'bg-white/10 border-l-2 border-[#D4AF37]' : ''}`}
                    data-testid={`lead-row-${lead.id}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="text-white font-medium truncate">{lead.name}</p>
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${getTypeColor(lead.type)}`}>
                            {lead.type}
                          </span>
                          {lead.assigned_to_name && (
                            <span className="flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-white/10 text-white/70">
                              <UserCircle className="w-3 h-3" />
                              {lead.assigned_to_name}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 text-sm text-white/50">
                          {lead.email && (
                            <span className="flex items-center gap-1 truncate">
                              <Mail className="w-3 h-3" />
                              {lead.email}
                            </span>
                          )}
                          {lead.company && (
                            <span className="flex items-center gap-1 truncate">
                              <Building2 className="w-3 h-3" />
                              {lead.company}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(lead.status)}`}>
                          {lead.status}
                        </span>
                        {lead.value > 0 && (
                          <span className="text-[#D4AF37] text-sm font-medium">€{lead.value}</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Right Panel - Lead Details */}
        <div className="space-y-4">
          {selectedLead ? (
            <>
              {/* Lead Info Card */}
              <div className="bg-[#0B0F19] rounded-xl border border-[#D4AF37]/10 overflow-hidden">
                <div className="p-4 border-b border-white/5 flex items-center justify-between">
                  <h4 className="font-semibold text-white">Lead Details</h4>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEditLead(selectedLead)}
                      className="text-white/50 hover:text-white"
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteLead(selectedLead.id)}
                      className="text-red-400 hover:text-red-300"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <div className="p-4 space-y-4">
                  <div>
                    <p className="text-2xl font-bold text-white">{selectedLead.name}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${getTypeColor(selectedLead.type)}`}>
                        {selectedLead.type}
                      </span>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(selectedLead.status)}`}>
                        {selectedLead.status}
                      </span>
                    </div>
                  </div>

                  <div className="space-y-2 text-sm">
                    {selectedLead.email && (
                      <a href={`mailto:${selectedLead.email}`} className="flex items-center gap-2 text-white/70 hover:text-[#D4AF37]">
                        <Mail className="w-4 h-4" />
                        {selectedLead.email}
                      </a>
                    )}
                    {selectedLead.phone && (
                      <a href={`tel:${selectedLead.phone}`} className="flex items-center gap-2 text-white/70 hover:text-[#D4AF37]">
                        <Phone className="w-4 h-4" />
                        {selectedLead.phone}
                      </a>
                    )}
                    {selectedLead.company && (
                      <p className="flex items-center gap-2 text-white/70">
                        <Building2 className="w-4 h-4" />
                        {selectedLead.company}
                      </p>
                    )}
                    {selectedLead.value > 0 && (
                      <p className="flex items-center gap-2 text-[#D4AF37] font-medium">
                        <DollarSign className="w-4 h-4" />
                        €{selectedLead.value} estimated value
                      </p>
                    )}
                    <p className="flex items-center gap-2 text-white/50">
                      <Tag className="w-4 h-4" />
                      Source: {selectedLead.source}
                    </p>
                    <p className="flex items-center gap-2 text-white/50">
                      <Clock className="w-4 h-4" />
                      Added {selectedLead.created_at ? new Date(selectedLead.created_at).toLocaleDateString() : 'N/A'}
                    </p>
                  </div>

                  {/* Quick Status Update */}
                  <div>
                    <Label className="text-white/50 text-xs mb-2 block">Quick Status Update</Label>
                    <div className="flex flex-wrap gap-1">
                      {['new', 'contacted', 'interested', 'converted', 'lost'].map((status) => (
                        <button
                          key={status}
                          onClick={() => onUpdateLeadStatus(selectedLead.id, status)}
                          className={`px-2 py-1 rounded text-xs font-medium transition-all ${selectedLead.status === status
                            ? getStatusColor(status) + ' ring-1 ring-white/30'
                            : 'bg-white/5 text-white/40 hover:bg-white/10'
                            }`}
                        >
                          {status}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Assign to Team Member */}
                  <div>
                    <Label className="text-white/50 text-xs mb-2 block">Assign To</Label>
                    <Select
                      value={selectedLead.assigned_to || "unassigned"}
                      onValueChange={(v) => onAssignLead(selectedLead.id, v === "unassigned" ? null : v)}
                    >
                      <SelectTrigger className="bg-white/5 border-white/10 text-white">
                        <div className="flex items-center gap-2">
                          {selectedLead.assigned_to ? (
                            <>
                              <div
                                className="w-5 h-5 rounded-full flex items-center justify-center text-white text-xs font-bold"
                                style={{ backgroundColor: teamMembers.find(m => m.id === selectedLead.assigned_to)?.avatar_color || '#D4AF37' }}
                              >
                                {getAvatarInitials(selectedLead.assigned_to_name)}
                              </div>
                              <span>{selectedLead.assigned_to_name}</span>
                            </>
                          ) : (
                            <>
                              <UserCircle className="w-5 h-5 text-white/40" />
                              <span className="text-white/50">Unassigned</span>
                            </>
                          )}
                        </div>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="unassigned">
                          <div className="flex items-center gap-2">
                            <UserCircle className="w-5 h-5 text-white/40" />
                            Unassigned
                          </div>
                        </SelectItem>
                        {teamMembers.map(member => (
                          <SelectItem key={member.id} value={member.id}>
                            <div className="flex items-center gap-2">
                              <div
                                className="w-5 h-5 rounded-full flex items-center justify-center text-white text-xs font-bold"
                                style={{ backgroundColor: member.avatar_color || '#D4AF37' }}
                              >
                                {getAvatarInitials(member.name)}
                              </div>
                              {member.name}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Convert to Business/Affiliate */}
                  {selectedLead.status !== 'converted' && (selectedLead.type === 'business' || selectedLead.type === 'affiliate') && (
                    <div className="pt-3 border-t border-white/10">
                      <Label className="text-white/50 text-xs mb-2 block">Convert Lead</Label>
                      {selectedLead.type === 'business' ? (
                        <Button
                          onClick={() => setShowConvertDialog(true)}
                          className="w-full bg-blue-500 hover:bg-blue-600 text-white"
                          data-testid="convert-to-business-btn"
                        >
                          <FileText className="w-4 h-4 mr-2" />
                          Convert to Business Partner
                        </Button>
                      ) : (
                        <Button
                          onClick={handleConvertToAffiliate}
                          disabled={converting}
                          className="w-full bg-purple-500 hover:bg-purple-600 text-white"
                          data-testid="convert-to-affiliate-btn"
                        >
                          {converting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserPlus className="w-4 h-4 mr-2" />}
                          Convert to Affiliate
                        </Button>
                      )}
                    </div>
                  )}

                  {/* Already Converted Info */}
                  {selectedLead.converted_to && (
                    <div className="pt-3 border-t border-white/10">
                      <p className="text-green-400 text-sm flex items-center gap-2">
                        <ChevronRight className="w-4 h-4" />
                        Converted to {selectedLead.converted_to === 'business_application' ? 'Business Application' : 'Affiliate'}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Notes Section */}
              <div className="bg-[#0B0F19] rounded-xl border border-[#D4AF37]/10 overflow-hidden">
                <div className="p-4 border-b border-white/5">
                  <h4 className="font-semibold text-white flex items-center gap-2">
                    <FileText className="w-4 h-4 text-[#D4AF37]" />
                    Notes
                  </h4>
                </div>
                <div className="p-4">
                  {/* Add Note */}
                  <div className="flex gap-2 mb-4">
                    <Input
                      value={leadNote}
                      onChange={(e) => setLeadNote(e.target.value)}
                      className="bg-white/5 border-white/10 text-white flex-1"
                      placeholder="Add a note..."
                      onKeyDown={(e) => e.key === 'Enter' && handleAddNote()}
                    />
                    <Button
                      onClick={handleAddNote}
                      disabled={!leadNote.trim()}
                      className="btn-gold"
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>

                  {/* Notes List */}
                  <div className="space-y-3 max-h-[300px] overflow-y-auto">
                    {selectedLead.notes && selectedLead.notes.length > 0 ? (
                      selectedLead.notes.slice().reverse().map((note, idx) => (
                        <div key={idx} className="bg-white/5 rounded-lg p-3">
                          <p className="text-white text-sm">{note.text}</p>
                          <p className="text-white/40 text-xs mt-1">
                            {note.created_at ? new Date(note.created_at).toLocaleString() : 'N/A'}
                          </p>
                        </div>
                      ))
                    ) : (
                      <p className="text-white/40 text-sm text-center py-4">No notes yet</p>
                    )}
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="bg-[#0B0F19] rounded-xl border border-[#D4AF37]/10 p-8 text-center">
              <MessageSquare className="w-12 h-12 mx-auto mb-3 text-white/20" />
              <p className="text-white/50">Select a lead to view details</p>
            </div>
          )}
        </div>
      </div>

      {/* Convert to Business Dialog */}
      <Dialog open={showConvertDialog} onOpenChange={setShowConvertDialog}>
        <DialogContent className="bg-[#0B0F19] border-[#D4AF37]/20 text-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Playfair_Display'] text-xl flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#D4AF37]" />
              Convert to Business Partner
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-white/60 text-sm">
              Convert <span className="text-white font-medium">{selectedLead?.company || selectedLead?.name}</span> to a business partner.
            </p>

            {/* AI Extract for Convert */}
            <div className="bg-gradient-to-r from-purple-500/10 to-blue-500/10 rounded-lg p-3 border border-purple-500/20">
              <Label className="text-purple-400 flex items-center gap-2 mb-2 text-sm">
                <Wand2 className="w-4 h-4" />
                Auto-fill from website (optional)
              </Label>
              <div className="flex gap-2">
                <Input
                  value={convertData.website}
                  onChange={(e) => setConvertData({ ...convertData, website: e.target.value })}
                  className="bg-white/5 border-white/10 text-white flex-1 text-sm"
                  placeholder="https://business-website.com"
                />
                <Button
                  onClick={handleAIExtract}
                  className="bg-purple-500 hover:bg-purple-600 text-white"
                  size="sm"
                >
                  <Wand2 className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <div>
              <Label className="text-white/70">Description *</Label>
              <textarea
                value={convertData.description}
                onChange={(e) => setConvertData({ ...convertData, description: e.target.value })}
                className="w-full bg-white/5 border border-white/10 text-white mt-1 rounded-md p-3 min-h-[60px] text-sm"
                placeholder="Business description..."
              />
            </div>

            <div>
              <Label className="text-white/70">Address *</Label>
              <Input
                value={convertData.address}
                onChange={(e) => setConvertData({ ...convertData, address: e.target.value })}
                className="bg-white/5 border-white/10 text-white mt-1"
                placeholder="Full address..."
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-white/70">Category</Label>
                <Select value={convertData.category} onValueChange={(v) => setConvertData({ ...convertData, category: v })}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="dining">Dining</SelectItem>
                    <SelectItem value="shopping">Shopping</SelectItem>
                    <SelectItem value="services">Services</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-white/70">Discount %</Label>
                <Input
                  type="number"
                  min="10"
                  max="20"
                  value={convertData.discount_level}
                  onChange={(e) => setConvertData({ ...convertData, discount_level: parseInt(e.target.value) })}
                  className="bg-white/5 border-white/10 text-white mt-1"
                />
              </div>
            </div>

            <div>
              <Label className="text-white/70">Location Area</Label>
              <Select value={convertData.sub_location || "none"} onValueChange={(v) => setConvertData({ ...convertData, sub_location: v === "none" ? "" : v })}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white mt-1">
                  <SelectValue placeholder="Select area" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No specific area</SelectItem>
                  {subLocations.map((loc) => (
                    <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => setShowConvertDialog(false)}
                className="flex-1 border-white/20 text-white hover:bg-white/5"
              >
                Cancel
              </Button>
              <Button
                onClick={handleProceedToAgreement}
                disabled={!convertData.description || !convertData.address}
                className="flex-1 btn-gold"
              >
                <FileText className="w-4 h-4 mr-2" />
                Proceed to Sign Agreement
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Partner Agreement Modal */}
      {showAgreement && selectedLead && (
        <PartnerAgreement
          businessName={selectedLead.company || selectedLead.name}
          contactName={selectedLead.name}
          onSign={handleAgreementSigned}
          onCancel={() => {
            setShowAgreement(false);
            setShowConvertDialog(true);
          }}
        />
      )}
    </>
  );
};

export default CrmTab;
